import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Filter, Trash2, Eye, DollarSign, CheckCircle, XCircle, Clock, CreditCard, Printer } from 'lucide-react';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/alerts';

import { transactionService } from '@/services/transactionService';
import { Transaction, TransactionFilters, SALE_STATUSES } from '@/types/transaction';
import { useAuth } from '@/context/AuthContext';
import { useCompany } from '@/context/CompanyContext';
import { companyService } from '@/services/companyService';
import AddSaleModal from '@/components/AddSaleModal';
import AddPaymentModal from '@/components/AddPaymentModal';

const Sales: React.FC = () => {
  const { hasPermission } = useAuth();
  const { company } = useCompany();
  const queryClient = useQueryClient();

  // State
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filters, setFilters] = useState<TransactionFilters>({
    search: '',
    estado: '',
    cliente_id: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Transaction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  // Queries
  const { data: salesData, isLoading, error, refetch: refetchSales } = useQuery({
    queryKey: ['sales', page, limit, filters],
    queryFn: () => transactionService.getSales({
      page,
      limit,
      search: filters.search || undefined,
      estado: filters.estado || undefined,
      cliente_id: filters.cliente_id !== '' ? Number(filters.cliente_id) : undefined,
      date_from: filters.date_from || undefined,
      date_to: filters.date_to || undefined
    }),
    placeholderData: (previousData) => previousData,
    staleTime: 30000, // 30 seconds
  });

  // Mutations
  const deleteSaleMutation = useMutation({
    mutationFn: (id: number) => transactionService.deleteTransaction(id),
    onSuccess: () => {
      showSuccess('Venta eliminada exitosamente');
      queryClient.invalidateQueries({ queryKey: ['sales'] });
    },
    onError: (error: any) => {
      showError(error.response?.data?.message || 'Error al eliminar venta');
    }
  });


  const addPaymentMutation = useMutation({
    mutationFn: ({ transactionId, paymentData }: { transactionId: number; paymentData: any }) => 
      transactionService.addPayment(transactionId, paymentData),
    onSuccess: () => {
      showSuccess('Pago registrado exitosamente');
      queryClient.invalidateQueries({ queryKey: ['sales'] });
      setShowPaymentModal(false);
      setSelectedSale(null);
    },
    onError: (error: any) => {
      showError(error.response?.data?.message || 'Error al registrar pago');
    }
  });

  const handlePaymentSubmit = async (paymentData: any) => {
    if (!selectedSale) return;
    
    addPaymentMutation.mutate({
      transactionId: selectedSale.id,
      paymentData
    });
  };

  // Event handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    refetchSales();
  };

  const handleFilterChange = (key: keyof TransactionFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleDeleteSale = (sale: Transaction) => {
    showDeleteConfirm(
      `¿Está seguro de eliminar la venta "${sale.numero}"?`,
      () => deleteSaleMutation.mutate(sale.id)
    );
  };


  const handleAddPayment = (sale: Transaction) => {
    setSelectedSale(sale);
    setShowPaymentModal(true);
  };

  const handleViewDetails = async (sale: Transaction) => {
    try {
      // Get detailed sale data with items
      const detailedSale = await transactionService.getTransactionById(sale.id);
      
      // Merge original sale data with detailed data to ensure we have everything
      const saleForModal = {
        ...detailedSale,
        updated_at: detailedSale.updated_at || sale.updated_at,
        created_at: detailedSale.created_at || sale.created_at,
        cliente: detailedSale.cliente || sale.cliente,
        vendedor: detailedSale.vendedor || sale.vendedor
      };
      
      setSelectedSale(saleForModal);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Error loading sale details:', error);
      showError('Error al cargar los detalles de la venta');
    }
  };

  const handlePrintSale = async (sale: Transaction) => {
    try {
      // Get detailed sale data with items
      const detailedSale = await transactionService.getTransactionById(sale.id);
      
      // Open print window
      const printWindow = window.open('', '_blank', 'width=800,height=1000,scrollbars=yes,resizable=yes');
      if (!printWindow) {
        showError('No se pudo abrir la ventana de impresión');
        return;
      }

      // Merge original sale data with detailed data to ensure we have everything
      const saleForPrint = {
        ...detailedSale,
        updated_at: detailedSale.updated_at || sale.updated_at,
        created_at: detailedSale.created_at || sale.created_at,
        cliente: detailedSale.cliente || sale.cliente,
        vendedor: detailedSale.vendedor || sale.vendedor
      };

      // Debug logo URL
      if (company?.logo) {
        console.log('Logo URL:', companyService.getFileUrl(company.logo));
      }

      // Generate POS ticket HTML  
      const ticketHTML = generatePOSTicket(saleForPrint, formatDate, company);
      
      printWindow.document.write(ticketHTML);
      printWindow.document.close();
      
      // Wait a bit for images to load before printing
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 1000);
      
    } catch (error) {
      console.error('Error printing sale:', error);
      showError('Error al imprimir la venta');
    }
  };

  const generatePOSTicket = (sale: Transaction, dateFormatter: (dateString: string) => string, companyData?: any): string => {
    const formatCurrency = (amount: number) => 
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: sale.moneda || 'GTQ',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Venta ${sale.numero}</title>
        <style>
          @page {
            size: auto;
            margin: 2mm;
          }
          
          body {
            font-family: 'Courier New', monospace;
            font-size: 11px;
            line-height: 1.3;
            margin: 0;
            padding: 4px;
            max-width: 100%;
            min-width: 58mm;
            background: white;
            color: black;
            box-sizing: border-box;
          }
          
          @media print {
            body {
              width: auto !important;
              max-width: none !important;
              font-size: 10px;
              padding: 2mm;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .company-name {
              font-size: 14px !important;
            }
            
            .ticket-type {
              font-size: 12px !important;
            }
            
            .grand-total {
              font-size: 12px !important;
            }
            
            .logo {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
          
          .header {
            text-align: center;
            border-bottom: 1px dashed #000;
            padding-bottom: 8px;
            margin-bottom: 8px;
          }
          
          .logo {
            max-width: 100px;
            max-height: 80px;
            object-fit: contain;
            margin: 0 auto 8px auto;
            display: block;
            border: none;
            background: transparent;
          }
          
          .logo-fallback {
            width: 80px;
            height: 60px;
            background: #f3f4f6;
            border: 2px dashed #d1d5db;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 8px auto;
            font-size: 10px;
            color: #6b7280;
            text-align: center;
          }
          
          @media print {
            .logo {
              max-width: 80px;
              max-height: 60px;
              margin-bottom: 6px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            
            .logo-fallback {
              width: 60px;
              height: 45px;
              font-size: 8px;
            }
          }
          
          .company-name {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 4px;
          }
          
          .ticket-type {
            font-size: 14px;
            font-weight: bold;
            margin: 8px 0;
          }
          
          .info-line {
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
            flex-wrap: wrap;
            word-break: break-word;
          }
          
          .info-line span:first-child {
            font-weight: bold;
            min-width: 30%;
          }
          
          .info-line span:last-child {
            text-align: right;
            max-width: 65%;
            word-wrap: break-word;
          }
          
          .items-header {
            border-top: 1px dashed #000;
            border-bottom: 1px dashed #000;
            padding: 4px 0;
            margin: 8px 0;
            text-align: center;
            font-weight: bold;
          }
          
          .item-line {
            margin: 4px 0;
            word-wrap: break-word;
            border-bottom: 1px dotted #ccc;
            padding-bottom: 2px;
          }
          
          .item-name {
            font-weight: bold;
            margin-bottom: 2px;
            word-wrap: break-word;
            hyphens: auto;
          }
          
          .item-details {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            flex-wrap: wrap;
          }
          
          .item-details span {
            white-space: nowrap;
          }
          
          .totals {
            border-top: 1px dashed #000;
            padding-top: 8px;
            margin-top: 8px;
          }
          
          .total-line {
            display: flex;
            justify-content: space-between;
            margin: 2px 0;
            flex-wrap: wrap;
          }
          
          .total-line span:first-child {
            font-weight: bold;
            min-width: 40%;
          }
          
          .total-line span:last-child {
            text-align: right;
            font-weight: bold;
          }
          
          .grand-total {
            font-weight: bold;
            font-size: 14px;
            border-top: 1px solid #000;
            padding-top: 4px;
            margin-top: 4px;
          }
          
          .footer {
            text-align: center;
            border-top: 1px dashed #000;
            padding-top: 8px;
            margin-top: 12px;
            font-size: 10px;
          }
          
          .status {
            text-align: center;
            font-weight: bold;
            margin: 8px 0;
            padding: 4px;
            border: 1px solid #000;
          }
        </style>
      </head>
      <body>
        <div class="header">
          ${companyData?.logo ? `
            <img 
              src="${companyService.getFileUrl(companyData.logo)}" 
              alt="Logo de la empresa" 
              class="logo" 
              onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
            />
            <div class="logo-fallback" style="display: none;">
              LOGO
            </div>
          ` : `
            <div class="logo-fallback">
              ${companyData?.name ? companyData.name.substring(0, 10) : 'EMPRESA'}
            </div>
          `}
          <div class="company-name">${companyData?.name || 'HOPE TRAVELS'}</div>
          ${companyData?.address ? `<div style="font-size: 10px; margin: 2px 0; line-height: 1.2;">${companyData.address}</div>` : ''}
          ${companyData?.phone ? `<div style="font-size: 10px; margin: 2px 0; font-weight: bold;">Tel: ${companyData.phone}</div>` : ''}
          ${companyData?.email ? `<div style="font-size: 9px; margin: 2px 0; color: #666;">${companyData.email}</div>` : ''}
        </div>
        
        <div class="ticket-type">${(sale.impuestos && sale.impuestos > 0) ? 'FACTURA' : 'RECIBO'}</div>
        
        <div class="info-line">
          <span>No.:</span>
          <span>${sale.numero}</span>
        </div>
        
        <div class="info-line">
          <span>Fecha:</span>
          <span>${dateFormatter(sale.updated_at || sale.created_at)}</span>
        </div>
        
        <div class="info-line">
          <span>Cliente:</span>
          <span>${sale.cliente?.name || 'N/A'}</span>
        </div>
        
        ${sale.cliente?.doc_number ? `
        <div class="info-line">
          <span>Documento:</span>
          <span>${sale.cliente.doc_number}</span>
        </div>
        ` : ''}
        
        <div class="info-line">
          <span>Vendedor:</span>
          <span>${sale.vendedor?.first_name || ''} ${sale.vendedor?.last_name || ''}</span>
        </div>
        
        <div class="status">
          ESTADO: ${transactionService.formatSaleStatus(sale.estado)}
        </div>
        
        <div class="items-header">
          ARTÍCULOS
        </div>
        
        ${sale.items && sale.items.length > 0 ? 
          sale.items.map(item => `
            <div class="item-line">
              <div class="item-name">${item.descripcion || 'Artículo sin descripción'}</div>
              <div class="item-details">
                <span>${Math.floor(item.cantidad || 0)} x ${formatCurrency(item.precio_unitario || 0)}</span>
                <span>${formatCurrency(item.total_linea || 0)}</span>
              </div>
              ${(item.descuento || 0) > 0 ? `
                <div class="item-details">
                  <span>Desc:</span>
                  <span>-${formatCurrency(item.descuento)}</span>
                </div>
              ` : ''}
            </div>
          `).join('') 
          : 
          '<div class="item-line"><div class="item-name">Sin artículos agregados</div></div>'
        }
        
        <div class="totals">
          <div class="total-line">
            <span>Subtotal:</span>
            <span>${formatCurrency(sale.subtotal)}</span>
          </div>
          
          ${sale.descuentos > 0 ? `
          <div class="total-line">
            <span>Descuentos:</span>
            <span>-${formatCurrency(sale.descuentos)}</span>
          </div>
          ` : ''}
          
          ${(sale.impuestos && sale.impuestos > 0) ? `
          <div class="total-line">
            <span>Impuestos:</span>
            <span>${formatCurrency(sale.impuestos)}</span>
          </div>
          ` : ''}
          
          <div class="total-line grand-total">
            <span>TOTAL:</span>
            <span>${formatCurrency(sale.total)}</span>
          </div>
          
          <div class="total-line">
            <span>Saldo:</span>
            <span>${formatCurrency(sale.saldo || 0)}</span>
          </div>
        </div>
        
        ${sale.condiciones_pago ? `
        <div class="info-line" style="margin-top: 8px;">
          <span>Condiciones:</span>
          <span>${sale.condiciones_pago}</span>
        </div>
        ` : ''}
        
        ${sale.nota ? `
        <div style="margin-top: 8px; padding-top: 8px; border-top: 1px dashed #000;">
          <div style="font-weight: bold;">Notas:</div>
          <div style="font-size: 10px; margin-top: 4px;">${sale.nota}</div>
        </div>
        ` : ''}
        
        <div class="footer">
          <div>¡Gracias por su compra!</div>
          <div style="margin-top: 4px;">${companyData?.name || 'HopeTravels'} - Sistema de Gestión</div>
          ${companyData?.phone ? `<div style="margin-top: 2px;">Tel: ${companyData.phone}</div>` : ''}
          <div style="margin-top: 2px;">${new Date().toLocaleString('es-GT')}</div>
        </div>
      </body>
      </html>
    `;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('es-ES');
    } catch (error) {
      return 'N/A';
    }
  };

  const getStatusBadge = (estado: string) => {
    const statusConfig: { [key: string]: { class: string; icon: React.ReactNode } } = {
      'PENDIENTE': { class: 'badge-warning', icon: <Clock className="h-3 w-3" /> },
      'PAGADA': { class: 'badge-success', icon: <CheckCircle className="h-3 w-3" /> },
      'REEMBOLSADA': { class: 'badge-info', icon: <CreditCard className="h-3 w-3" /> },
      'ANULADA': { class: 'badge-danger', icon: <XCircle className="h-3 w-3" /> }
    };

    const config = statusConfig[estado] || { class: 'badge-gray', icon: null };

    return (
      <span className={`badge ${config.class} flex items-center gap-1`}>
        {config.icon}
        {transactionService.formatSaleStatus(estado)}
      </span>
    );
  };

  const getPaymentStatusBadge = (sale: Transaction) => {
    const isPaid = sale.saldo <= 0;
    const isPartiallyPaid = sale.saldo < sale.total && sale.saldo > 0;

    if (isPaid) {
      return <span className="badge badge-success text-xs">Pagado</span>;
    } else if (isPartiallyPaid) {
      return <span className="badge badge-warning text-xs">Parcial</span>;
    } else {
      return <span className="badge badge-danger text-xs">Pendiente</span>;
    }
  };

  const canPerformAction = (action: string, sale: Transaction) => {
    if (!hasPermission(action)) return false;
    
    // Business logic for sale actions
    switch (action) {
      case 'add_payment':
        return sale.estado === 'PENDIENTE' && sale.saldo > 0;
      case 'cancel':
        return sale.estado !== 'ANULADA';
      case 'refund':
        return sale.estado === 'PAGADA';
      default:
        return true;
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <p className="text-red-600">Error al cargar las ventas</p>
            <button
              onClick={() => refetchSales()}
              className="btn-secondary mt-2"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Ventas</h1>
              <p className="mt-1 text-sm text-gray-600">
                Gestiona las ventas de la empresa
              </p>
            </div>
            {hasPermission('create_sale') && (
              <button
                onClick={() => setShowAddModal(true)}
                className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <Plus className="h-5 w-5" />
                <span className="sm:inline">Nueva Venta</span>
              </button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar por número, cliente..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="form-input pl-10 w-full"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className="btn-secondary flex items-center gap-2 flex-1 sm:flex-initial justify-center"
              >
                <Filter className="h-5 w-5" />
                <span>Filtros</span>
              </button>
              <button
                type="submit"
                className="btn-primary flex-1 sm:flex-initial"
              >
                Buscar
              </button>
            </div>
          </form>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">
                    Estado
                  </label>
                  <select
                    value={filters.estado}
                    onChange={(e) => handleFilterChange('estado', e.target.value)}
                    className="form-select"
                  >
                    <option value="">Todos los estados</option>
                    {SALE_STATUSES.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">
                    Fecha Desde
                  </label>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <label className="form-label">
                    Fecha Hasta
                  </label>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      search: '',
                      estado: '',
                      cliente_id: '',
                      date_from: '',
                      date_to: ''
                    });
                    setPage(1);
                  }}
                  className="btn-secondary w-full"
                >
                  Limpiar Filtros
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Cargando ventas...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">
              <p>Error al cargar las ventas</p>
              <button
                onClick={refetchSales}
                className="btn-secondary mt-2"
              >
                Reintentar
              </button>
            </div>
          ) : !salesData?.transactions.length ? (
            <div className="p-8 text-center text-gray-500">
              <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No se encontraron ventas</p>
              {hasPermission('create_sale') && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="btn-primary mt-4"
                >
                  Crear primera venta
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Sales Table - Desktop */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Venta</th>
                      <th className="table-header-cell">Cliente</th>
                      <th className="table-header-cell">Estado</th>
                      <th className="table-header-cell">Pago</th>
                      <th className="table-header-cell">Total</th>
                      <th className="table-header-cell">Saldo</th>
                      <th className="table-header-cell">Fecha</th>
                      {hasPermission('manage_sales') && (
                        <th className="table-header-cell text-right">Acciones</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {salesData.transactions.map((sale) => (
                      <tr key={sale.id} className="hover:bg-gray-50">
                        <td className="table-cell">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {sale.numero}
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {sale.cliente?.name || sale.customer?.name || 'Sin cliente'}
                            </div>
                            {(sale.cliente?.doc_number || sale.customer?.doc_number) && (
                              <div className="text-sm text-gray-500">
                                {sale.cliente?.doc_number || sale.customer?.doc_number}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="table-cell">
                          {getStatusBadge(sale.estado)}
                        </td>
                        <td className="table-cell">
                          {getPaymentStatusBadge(sale)}
                        </td>
                        <td className="table-cell">
                          <span className="text-sm font-medium text-gray-900">
                            {transactionService.formatCurrency(sale.total)}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className={`text-sm font-medium ${sale.saldo > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {transactionService.formatCurrency(sale.saldo)}
                          </span>
                        </td>
                        <td className="table-cell text-gray-500">
                          {formatDate(sale.created_at)}
                        </td>
                        {hasPermission('manage_sales') && (
                          <td className="table-cell text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleViewDetails(sale)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                title="Ver detalles"
                              >
                                <Eye className="h-4 w-4" />
                              </button>

                              <button
                                onClick={() => handlePrintSale(sale)}
                                className="text-gray-600 hover:text-gray-900 p-1"
                                title="Imprimir"
                              >
                                <Printer className="h-4 w-4" />
                              </button>

                              {canPerformAction('add_payment', sale) && (
                                <button
                                  onClick={() => handleAddPayment(sale)}
                                  className="text-green-600 hover:text-green-900 p-1"
                                  title="Agregar pago"
                                >
                                  <CreditCard className="h-4 w-4" />
                                </button>
                              )}


                              {hasPermission('delete_sale') && sale.saldo > 0 && (
                                <button
                                  onClick={() => handleDeleteSale(sale)}
                                  className="text-red-600 hover:text-red-900 p-1"
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Sales Cards - Mobile */}
              <div className="lg:hidden space-y-4 p-4">
                {salesData.transactions.map((sale) => (
                  <div key={sale.id} className="card">
                    <div className="card-body">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-medium text-gray-900">
                              {sale.numero}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-500">
                            {sale.cliente?.name || sale.customer?.name || 'Sin cliente'}
                          </p>
                        </div>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(sale.estado)}
                          {getPaymentStatusBadge(sale)}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Total</p>
                          <p className="font-medium text-gray-900">{transactionService.formatCurrency(sale.total)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Saldo</p>
                          <p className={`font-medium ${sale.saldo > 0 ? 'text-red-600' : 'text-green-600'}`}>{transactionService.formatCurrency(sale.saldo)}</p>
                        </div>
                        {(sale.cliente?.doc_number || sale.customer?.doc_number) && (
                          <div className="col-span-2">
                            <p className="text-gray-500">Documento</p>
                            <p className="font-medium text-gray-900">{sale.cliente?.doc_number || sale.customer?.doc_number}</p>
                          </div>
                        )}
                        <div className="col-span-2">
                          <p className="text-gray-500">Fecha</p>
                          <p className="font-medium text-gray-900">{formatDate(sale.created_at)}</p>
                        </div>
                      </div>

                      {hasPermission('manage_sales') && (
                        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => handleViewDetails(sale)}
                            className="btn-sm btn-outline flex items-center gap-2 flex-1"
                          >
                            <Eye className="h-4 w-4" />
                            Ver
                          </button>

                          <div className="flex gap-1">
                            <button
                              onClick={() => handlePrintSale(sale)}
                              className="text-gray-600 hover:text-gray-900 p-2"
                              title="Imprimir"
                            >
                              <Printer className="h-4 w-4" />
                            </button>

                            {canPerformAction('add_payment', sale) && (
                              <button
                                onClick={() => handleAddPayment(sale)}
                                className="text-green-600 hover:text-green-900 p-2"
                                title="Agregar pago"
                              >
                                <CreditCard className="h-4 w-4" />
                              </button>
                            )}


                            {hasPermission('delete_sale') && sale.saldo > 0 && (
                              <button
                                onClick={() => handleDeleteSale(sale)}
                                className="text-red-600 hover:text-red-900 p-2"
                                title="Eliminar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {salesData.pagination.pages > 1 && (
                <div className="px-4 sm:px-6 py-3 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-700 text-center sm:text-left">
                      Mostrando {((page - 1) * limit) + 1} a {Math.min(page * limit, salesData.pagination.total)} de {salesData.pagination.total} ventas
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={page <= 1}
                        className="btn-secondary btn-sm disabled:opacity-50"
                      >
                        <span className="hidden sm:inline">Anterior</span>
                        <span className="sm:hidden">‹</span>
                      </button>
                      <span className="text-sm text-gray-700 px-2">
                        <span className="hidden sm:inline">Página </span>{page} / {salesData.pagination.pages}
                      </span>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={page >= salesData.pagination.pages}
                        className="btn-secondary btn-sm disabled:opacity-50"
                      >
                        <span className="hidden sm:inline">Siguiente</span>
                        <span className="sm:hidden">›</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <>
        {/* Detail Modal */}
        {showDetailModal && selectedSale && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Detalles de Venta</h2>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="h-6 w-6" />
                  </button>
                </div>

                {/* Header Information */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="text-center mb-4">
                    {company?.logo && (
                      <div className="mb-4">
                        <img
                          src={companyService.getFileUrl(company.logo)}
                          alt="Logo de la empresa"
                          className="max-w-[120px] max-h-[90px] object-contain mx-auto drop-shadow-sm"
                          style={{
                            filter: 'contrast(1.1) brightness(0.95)'
                          }}
                        />
                      </div>
                    )}
                    <h3 className="text-lg font-bold text-gray-900">{company?.name || 'HOPE TRAVELS'}</h3>
                    {company?.address && <p className="text-sm text-gray-600">{company.address}</p>}
                    {company?.phone && <p className="text-sm text-gray-600">Tel: {company.phone}</p>}
                    {company?.email && <p className="text-sm text-gray-600">{company.email}</p>}
                    <div className="mt-2 inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full font-semibold">
                      {(selectedSale.impuestos && selectedSale.impuestos > 0) ? 'FACTURA' : 'RECIBO'}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-semibold">No.:</span>
                        <span className="font-mono">{selectedSale.numero}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold">Fecha:</span>
                        <span>{formatDate(selectedSale.created_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold">Cliente:</span>
                        <span>{selectedSale.cliente?.name || selectedSale.customer?.name || 'N/A'}</span>
                      </div>
                      {(selectedSale.cliente?.doc_number || selectedSale.customer?.doc_number) && (
                        <div className="flex justify-between">
                          <span className="font-semibold">Documento:</span>
                          <span>{selectedSale.cliente?.doc_number || selectedSale.customer?.doc_number}</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-semibold">Vendedor:</span>
                        <span>{selectedSale.vendedor?.first_name} {selectedSale.vendedor?.last_name || ''}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Estado:</span>
                        <span>{getStatusBadge(selectedSale.estado)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Pago:</span>
                        <span>{getPaymentStatusBadge(selectedSale)}</span>
                      </div>
                      {selectedSale.condiciones_pago && (
                        <div className="flex justify-between">
                          <span className="font-semibold">Condiciones:</span>
                          <span>{selectedSale.condiciones_pago}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="font-semibold">Saldo:</span>
                        <span className={`font-bold ${selectedSale.saldo > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {transactionService.formatCurrency(selectedSale.saldo)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items Section */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center border-b pb-2">
                    ARTÍCULOS
                  </h4>

                  <div className="space-y-3">
                    {selectedSale.items && selectedSale.items.length > 0 ? (
                      selectedSale.items.map((item, index) => (
                        <div key={index} className="border-b border-gray-100 pb-3">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">{item.descripcion || 'Artículo sin descripción'}</h5>
                              <div className="text-sm text-gray-600 mt-1">
                                {Math.floor(item.cantidad || 0)} x {transactionService.formatCurrency(item.precio_unitario || 0)}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-gray-900">
                                {transactionService.formatCurrency(item.total_linea || 0)}
                              </div>
                              {(item.descuento || 0) > 0 && (
                                <div className="text-sm text-red-600">
                                  Desc: -{transactionService.formatCurrency(item.descuento)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-4">
                        Sin artículos agregados
                      </div>
                    )}
                  </div>
                </div>

                {/* Totals Section */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 text-center border-b pb-2">
                    TOTALES
                  </h4>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-semibold">Subtotal:</span>
                      <span>{transactionService.formatCurrency(selectedSale.subtotal)}</span>
                    </div>

                    {(selectedSale.descuentos && selectedSale.descuentos > 0) && (
                      <div className="flex justify-between text-red-600">
                        <span className="font-semibold">Descuentos:</span>
                        <span>-{transactionService.formatCurrency(selectedSale.descuentos)}</span>
                      </div>
                    )}

                    {(selectedSale.impuestos && selectedSale.impuestos > 0) && (
                      <div className="flex justify-between">
                        <span className="font-semibold">Impuestos:</span>
                        <span>{transactionService.formatCurrency(selectedSale.impuestos)}</span>
                      </div>
                    )}

                    <div className="flex justify-between text-lg font-bold text-gray-900 border-t pt-2 mt-3">
                      <span>TOTAL:</span>
                      <span>{transactionService.formatCurrency(selectedSale.total)}</span>
                    </div>

                    <div className={`flex justify-between text-lg font-bold ${selectedSale.saldo > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      <span>SALDO:</span>
                      <span>{transactionService.formatCurrency(selectedSale.saldo)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                {selectedSale.nota && (
                  <div className="mt-6 bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Notas:</h4>
                    <p className="text-sm text-gray-700">{selectedSale.nota}</p>
                  </div>
                )}

                {/* Footer */}
                <div className="mt-6 text-center text-sm text-gray-500 border-t pt-4">
                  <p>¡Gracias por su compra!</p>
                  <p>{company?.name || 'HopeTravels'} - Sistema de Gestión</p>
                  {company?.phone && <p>Tel: {company.phone}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedSale && (
          <AddPaymentModal
            isOpen={showPaymentModal}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedSale(null);
            }}
            transaction={selectedSale}
            onPaymentAdded={handlePaymentSubmit}
          />
        )}

        {/* Add Sale Modal */}
        <AddSaleModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
        />
      </>
    </div>
  );
};

export default Sales;